import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronDown, ChevronUp, Download, CreditCard, Loader2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [retryingPayment, setRetryingPayment] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Auto-refresh para pedidos pendentes
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const hasPendingOrders = orders.some(order => order.payment_status === 'pending');
    
    if (hasPendingOrders) {
      const intervalId = setInterval(() => {
        fetchOrders(true); // true = silent refresh (sem loading)
      }, 30000); // 30 segundos
      
      return () => clearInterval(intervalId);
    }
  }, [orders, autoRefreshEnabled]);

  const fetchOrders = async (silentRefresh = false) => {
    try {
      if (!silentRefresh) {
        setLoading(true);
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/customer/orders`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      
      const newOrders = response.data.orders || [];
      
      // Verificar se algum pedido mudou de status
      if (silentRefresh && orders.length > 0) {
        orders.forEach((oldOrder) => {
          const newOrder = newOrders.find(o => o.id === oldOrder.id);
          if (newOrder && oldOrder.payment_status !== newOrder.payment_status) {
            // Mostrar notificação de mudança de status
            if (newOrder.payment_status === 'paid') {
              alert(`✅ Ótima notícia! O pagamento do pedido #${oldOrder.id.substring(0, 8).toUpperCase()} foi confirmado!`);
            }
          }
        });
      }
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    } finally {
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { label: 'Pago', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-800' },
      cancelled: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRetryPayment = async (orderId) => {
    setRetryingPayment(orderId);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/customer/retry-payment/${orderId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }
      );

      // Abrir Mercado Pago em nova aba
      if (response.data.initPoint) {
        window.open(response.data.initPoint, '_blank');
        alert('Uma nova aba foi aberta com o pagamento do Mercado Pago. Complete o pagamento e atualize esta página para ver o status atualizado.');
      }
    } catch (error) {
      console.error('Erro ao retomar pagamento:', error);
      alert(error.response?.data?.detail || 'Erro ao retomar pagamento. Tente novamente.');
    } finally {
      setRetryingPayment(null);
    }
  };

  const handleCheckPaymentStatus = async (orderId) => {
    setCheckingStatus(orderId);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/check-payment-status/${orderId}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }
      );

      if (response.data.updated) {
        // Status foi atualizado - recarregar pedidos
        await fetchOrders();
        alert(`✅ Status atualizado! O pagamento está ${response.data.status === 'paid' ? 'CONFIRMADO' : response.data.status.toUpperCase()}`);
      } else {
        alert(`ℹ️ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      alert(error.response?.data?.detail || 'Erro ao verificar status do pagamento. Tente novamente.');
    } finally {
      setCheckingStatus(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <p className="text-center text-gray-500">Carregando pedidos...</p>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card className="p-8">
        <p className="text-center text-gray-500">Você ainda não fez nenhum pedido.</p>
      </Card>
    );
  }

  const hasPendingOrders = orders.some(order => order.payment_status === 'pending');

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Meus Pedidos</h2>
        
        {hasPendingOrders && autoRefreshEnabled && (
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg">
            <RefreshCw size={16} className="animate-spin text-blue-600" />
            <span>Atualizando automaticamente...</span>
          </div>
        )}
      </div>
      
      {orders.map((order) => (
        <Card key={order.id} className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-lg text-gray-800">{order.project_name}</h3>
              <p className="text-sm text-gray-500">Pedido #{order.id.substring(0, 8).toUpperCase()}</p>
              <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
            </div>
            <div className="text-right">
              {getStatusBadge(order.payment_status)}
              <p className="text-xl font-bold text-[#00A86B] mt-2">R$ {formatCurrency(order.total)}</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              className="w-full flex items-center justify-between"
            >
              <span>Ver detalhes</span>
              {expandedOrder === order.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>

            {expandedOrder === order.id && (
              <div className="mt-4 space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Itens do Pedido:</h4>
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm py-1">
                      <span className="text-gray-600">{item.service_name}</span>
                      <span className="text-gray-800">R$ {formatCurrency(item.price)}</span>
                    </div>
                  ))}
                </div>

                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto</span>
                    <span>- R$ {formatCurrency(order.discount)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>R$ {formatCurrency(order.total)}</span>
                </div>

                {order.payment_status === 'paid' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-green-800 font-semibold mb-2">
                      ✓ Pagamento confirmado! Você receberá os documentos técnicos por email.
                    </p>
                  </div>
                )}

                {order.payment_status === 'pending' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-yellow-800 mb-3">
                      ⏳ Aguardando confirmação do pagamento.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleRetryPayment(order.id)}
                        disabled={retryingPayment === order.id || checkingStatus === order.id}
                        className="bg-[#00A86B] hover:bg-[#00A86B]/90 text-white flex-1 min-w-[180px]"
                        size="sm"
                      >
                        {retryingPayment === order.id ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Abrindo...
                          </>
                        ) : (
                          <>
                            <CreditCard size={16} className="mr-2" />
                            Retomar Pagamento
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleCheckPaymentStatus(order.id)}
                        disabled={checkingStatus === order.id || retryingPayment === order.id}
                        variant="outline"
                        className="border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B]/10 flex-1 min-w-[180px]"
                        size="sm"
                      >
                        {checkingStatus === order.id ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Verificando...
                          </>
                        ) : (
                          <>
                            <RefreshCw size={16} className="mr-2" />
                            Verificar Status
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-yellow-700 mt-3">
                      💡 Se você já completou o pagamento, clique em "Verificar Status" para atualizar. Caso tenha fechado a aba, use "Retomar Pagamento".
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

export default MyOrders;